import type { SloMetric } from '@cast/features-cluster-shared-utils';
import { ClusterDisplayStatus } from '@cast/types';
import { formatPercentage } from '@cast/utils';

const HEATMAP_MIN_VALUE = 18;
const HEATMAP_FALLBACK_MIN = 18;
const HEATMAP_FALLBACK_MAX = 32;

type NumberLike = {
  toNumber(): number;
};

type ClusterSummaryLike = {
  nodesCountTotal?: NumberLike | null;
};

type ClusterEstimatedSavingsLike = {
  monthlySpend?: number | null;
  wastePercentage?: number | null;
};

export type HeatmapViewMode = 'compute' | 'health';

export type SloState = 'ok' | 'risk' | 'breach' | 'gathering';

type SloHeatmapData = {
  count: number;
  state: SloState;
};

const DISPLAY_STATUS_LABEL: Partial<Record<ClusterDisplayStatus, string>> = {
  [ClusterDisplayStatus.WAITING_CONNECTION]: 'Waiting for connection',
  [ClusterDisplayStatus.CONNECTING]: 'Connecting',
  [ClusterDisplayStatus.READ_ONLY]: 'Read only',
  [ClusterDisplayStatus.READY]: 'Connected',
  [ClusterDisplayStatus.HIBERNATED]: 'Hibernated',
  [ClusterDisplayStatus.HIBERNATING]: 'Hibernating',
  [ClusterDisplayStatus.RESUMING]: 'Resuming',
  [ClusterDisplayStatus.DELETED]: 'Deleted',
  [ClusterDisplayStatus.DELETING]: 'Deleting',
  [ClusterDisplayStatus.WARNING]: 'Warning',
  [ClusterDisplayStatus.FAILED]: 'Failed',
  [ClusterDisplayStatus.NOT_FOUND]: 'Not found',
  [ClusterDisplayStatus.ONLINE]: 'Online',
  [ClusterDisplayStatus.DISCONNECTED]: 'Disconnected',
  [ClusterDisplayStatus.DISCONNECTING]: 'Disconnecting',
  [ClusterDisplayStatus.NOT_RESPONDING]: 'Not Responding',
  [ClusterDisplayStatus.DISCOVERED]: 'Discovered',
  [ClusterDisplayStatus.ARCHIVED]: 'Archived',
};

const getHeatmapDisplayStatusLabel = (
  status?: ClusterDisplayStatus
): string | undefined => (status ? DISPLAY_STATUS_LABEL[status] : undefined);

export type ClusterHeatmapItem = {
  color: string;
  dashedBorder?: boolean;
  displayStatus?: ClusterDisplayStatus;
  displayStatusLabel?: string;
  footerLabel: string;
  id: string;
  monthlySpend?: number | null;
  name: string;
  nodeCount: number;
  reliabilityMetrics?: SloMetric[];
  sloState?: SloState;
  textColor: string;
  value: number;
  wastePercentage?: number | null;
};

type ClusterLike = {
  displayStatus?: ClusterDisplayStatus;
  id?: string;
  name: string;
};

// Disconnected and Not found clusters are excluded from the heat-map because
// they have no node/waste/SLO data to visualize, which would otherwise render
// as grey tiles of arbitrary size. The cluster table is unaffected.
export const isExcludedFromHeatmap = (
  status: ClusterDisplayStatus | undefined
): boolean =>
  status === ClusterDisplayStatus.DISCONNECTED ||
  status === ClusterDisplayStatus.NOT_FOUND;

const SLO_SORT_PRIORITY: Record<SloState, number> = {
  breach: 0,
  risk: 1,
  ok: 2,
  gathering: 3,
};

const mix = (a: number[], b: number[], t: number): number[] =>
  a.map((value, index) => value + (b[index] - value) * t);

const luminance = (rgb: number[]): number => {
  const channels = rgb.map((value) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
};

const contrastInk = (rgb: number[]): string =>
  luminance(rgb) > 0.22 ? '#1C2B4A' : '#ffffff';

const WASTE_INK_LUMINANCE_THRESHOLD = 0.42;

const wasteInk = (rgb: number[]): string =>
  luminance(rgb) > WASTE_INK_LUMINANCE_THRESHOLD ? '#1C2B4A' : '#ffffff';

const rgbToCss = (rgb: number[]): string =>
  `rgb(${rgb.map((value) => Math.round(value)).join(',')})`;

const stableSize = (
  str: string,
  min = HEATMAP_FALLBACK_MIN,
  max = HEATMAP_FALLBACK_MAX
): number => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
  }

  return min + (Math.abs(h) % (max - min + 1));
};

const formatCompactMonthlySpend = (value?: number | null): string | null => {
  if (value == null || value <= 0) {
    return null;
  }

  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M/mo`;
  }

  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}k/mo`;
  }

  return `$${Math.round(value)}/mo`;
};

const computeWasteRgb = (waste: number | null | undefined): number[] => {
  if (waste == null) {
    return [222, 228, 237];
  }

  const t = Math.max(0, Math.min(1, waste / 78));
  const lo = [220, 232, 251];
  const hi = [29, 79, 181];

  return mix(lo, hi, Math.pow(t, 0.92));
};

export const computeWasteColors = (
  waste: number | null | undefined
): { fill: string; ink: string } => {
  const rgb = computeWasteRgb(waste);

  return {
    fill: rgbToCss(rgb),
    ink: waste == null ? '#48515f' : wasteInk(rgb),
  };
};

export const computeSloColors = (
  state: SloState,
  count = 1
): { fill: string; ink: string } => {
  if (state === 'gathering') {
    return { fill: '#dde2e9', ink: '#48515f' };
  }

  if (state === 'ok') {
    return { fill: '#4ed996', ink: contrastInk([78, 217, 150]) };
  }

  const severity = Math.max(0, Math.min(1, (count - 1) / 6));

  if (state === 'breach') {
    const base = mix([247, 206, 202], [201, 52, 46], severity);
    return { fill: rgbToCss(base), ink: contrastInk(base) };
  }

  const base = mix([250, 235, 200], [221, 160, 42], severity);
  return { fill: rgbToCss(base), ink: contrastInk(base) };
};

const sloFooterLabel = (state: SloState): string => {
  switch (state) {
    case 'breach':
      return 'Unhealthy';
    case 'risk':
      return 'At risk';
    case 'ok':
      return 'Healthy';
    default:
      return 'No data';
  }
};

const FALLBACK_SLO: SloHeatmapData = { state: 'gathering', count: 0 };

export const buildClusterHeatmapData = ({
  clusters,
  estimatedSavingsMap,
  fallbackSummaryMap,
  mode = 'compute',
  reliabilityMetricsMap,
  sloMap,
  summaryMap,
}: {
  clusters: ClusterLike[];
  estimatedSavingsMap: ReadonlyMap<string, ClusterEstimatedSavingsLike>;
  fallbackSummaryMap: ReadonlyMap<string, ClusterSummaryLike>;
  mode?: HeatmapViewMode;
  reliabilityMetricsMap?: ReadonlyMap<string, SloMetric[]>;
  sloMap?: ReadonlyMap<string, SloHeatmapData>;
  summaryMap: ReadonlyMap<string, ClusterSummaryLike>;
}): ClusterHeatmapItem[] => {
  const seenClusterKeys = new Set<string>();
  const uniqueClusters = clusters.filter(
    (cluster): cluster is ClusterLike & { id: string } => {
      if (!cluster.id || seenClusterKeys.has(cluster.id)) {
        return false;
      }

      seenClusterKeys.add(cluster.id);
      return true;
    }
  );

  const visibleClusters = uniqueClusters.filter(
    (cluster) => !isExcludedFromHeatmap(cluster.displayStatus)
  );

  const items = visibleClusters.map((cluster) => {
    const summary =
      summaryMap.get(cluster.id) ?? fallbackSummaryMap.get(cluster.id);
    const estimatedSavings = estimatedSavingsMap.get(cluster.id);
    const nodeCount = summary?.nodesCountTotal?.toNumber() ?? 0;
    const value =
      nodeCount > 0
        ? Math.max(HEATMAP_MIN_VALUE, Math.round(Math.sqrt(nodeCount) * 12))
        : stableSize(cluster.id);
    const wastePercentage = estimatedSavings?.wastePercentage ?? null;
    const monthlySpend = estimatedSavings?.monthlySpend ?? null;
    const isDiscovered =
      cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;

    let color: string;
    let textColor: string;
    let footerLabel: string;
    const dashedBorder = false;
    let sloState: SloState | undefined;

    if (mode === 'compute') {
      const wasteColors = computeWasteColors(wastePercentage);
      color = wasteColors.fill;
      textColor = wasteColors.ink;

      if (wastePercentage == null) {
        footerLabel = 'No data';
      } else {
        const spendLabel = formatCompactMonthlySpend(monthlySpend);
        footerLabel = `${formatPercentage(wastePercentage, {
          roundBy: 0,
        })} waste${
          spendLabel ? ` · ${isDiscovered ? '~' : ''}${spendLabel}` : ''
        }`;
      }
    } else {
      const sloData = sloMap?.get(cluster.id) ?? FALLBACK_SLO;
      sloState = sloData.state;
      const sloColors = computeSloColors(sloData.state, sloData.count);
      color = sloColors.fill;
      textColor = sloColors.ink;
      footerLabel = sloFooterLabel(sloData.state);
    }

    return {
      color,
      dashedBorder,
      displayStatus: cluster.displayStatus,
      displayStatusLabel: getHeatmapDisplayStatusLabel(cluster.displayStatus),
      footerLabel,
      id: cluster.id,
      monthlySpend,
      name: cluster.name,
      nodeCount,
      reliabilityMetrics:
        mode === 'health'
          ? reliabilityMetricsMap?.get(cluster.id) ?? []
          : undefined,
      sloState,
      textColor,
      value,
      wastePercentage,
    };
  });

  return items.sort((left, right) => {
    if (mode === 'health') {
      const leftPriority = SLO_SORT_PRIORITY[left.sloState ?? 'gathering'];
      const rightPriority = SLO_SORT_PRIORITY[right.sloState ?? 'gathering'];

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }
    }

    if (mode === 'compute') {
      const leftWaste = left.wastePercentage ?? -1;
      const rightWaste = right.wastePercentage ?? -1;

      if (rightWaste !== leftWaste) {
        return rightWaste - leftWaste;
      }
    }

    if (right.nodeCount !== left.nodeCount) {
      return right.nodeCount - left.nodeCount;
    }

    return left.name.localeCompare(right.name);
  });
};
